/* ASUS microsite mockups: interaction and motion. Native page scroll only, no wheel capture. */
(function () {
  "use strict";
  var roots = document.querySelectorAll("[data-ams]");
  if (!roots.length) return;

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var G = window.gsap, ST = window.ScrollTrigger, FL = window.Flip;
  var hasGsap = !!(G && ST && FL);
  if (hasGsap) G.registerPlugin(ST, FL);
  var motion = !reduce;

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  /* hand the resting state back to CSS. Plain DOM removal: gsap.set({clearProps:"transform"})
     was measured re-adding translate3d(0,0,0), which silently killed the CSS hover lift */
  function settle(els) {
    each(els || [], function (el) {
      ["transform", "translate", "rotate", "scale", "opacity"].forEach(function (p) { el.style.removeProperty(p); });
    });
  }
  function $all(root, sel) { return root.querySelectorAll(sel); }
  function plural(n, noun) { return n + " " + noun + (n === 1 ? "" : "s"); }

  each(roots, function (root) {
    initReveal(root);
    initFilters(root);
    initStage(root);
    initHero(root);
    initCompare(root);
    initSort(root);
    initAnchors(root);
  });

  if (hasGsap) {
    window.addEventListener("load", function () { ST.refresh(); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ST.refresh(); });
  }

  /* one-shot reveals: the hidden state only exists once observers are in place */
  function initReveal(root) {
    if (!motion || !("IntersectionObserver" in window)) return;
    var els = $all(root, "[data-ams-reveal]");
    var counter = new Map();
    each(els, function (el) {
      var key = el.parentNode;
      var i = counter.get(key) || 0;
      el.style.setProperty("--i", Math.min(i, 6));
      counter.set(key, i + 1);
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.04 });
    each(els, function (el) { io.observe(el); });
    root.classList.add("ams-motion");
  }

  /* selection: chips filter one or more item containers; DOM state (hidden, aria-pressed) is the truth */
  function initFilters(root) {
    each($all(root, "[data-ams-filter-group]"), function (group) {
      var gid = group.getAttribute("data-ams-filter-group") || "main";
      var buttons = $all(group, "[data-ams-filter]");
      var targets = (group.getAttribute("data-ams-targets") || "").split(",")
        .map(function (s) { return s.trim(); }).filter(Boolean)
        .map(function (sel) { return root.querySelector(sel); }).filter(Boolean);
      var statusSel = group.getAttribute("data-ams-status");
      var status = statusSel ? root.querySelector(statusSel) : null;
      var guideSel = group.getAttribute("data-ams-guide");
      var guide = guideSel ? root.querySelector(guideSel) : null;

      each(buttons, function (btn) {
        btn.addEventListener("click", function () { apply(btn); });
      });

      function apply(btn) {
        var key = btn.getAttribute("data-ams-filter");
        each(buttons, function (b) { b.setAttribute("aria-pressed", b === btn ? "true" : "false"); });
        var items = [];
        targets.forEach(function (t) { each($all(t, "[data-ams-item]"), function (it) { items.push(it); }); });
        var animate = hasGsap && motion;
        var state = animate ? FL.getState(items) : null;
        var parts = [];
        var none = [];
        targets.forEach(function (t) {
          var shown = 0;
          t._amsKeys = t._amsKeys || {};
          t._amsKeys[gid] = key;
          var keys = t._amsKeys;
          each($all(t, "[data-ams-item]"), function (it) {
            var tags = " " + (it.getAttribute("data-ams-tags") || "") + " ";
            var match = Object.keys(keys).every(function (g) { return keys[g] === "all" || tags.indexOf(" " + keys[g] + " ") > -1; });
            it.hidden = !match;
            if (match) { shown++; it.classList.add("is-in"); }
          });
          var empty = t.querySelector("[data-ams-empty]");
          if (empty) empty.hidden = shown !== 0;
          var noun = t.getAttribute("data-ams-noun");
          if (noun && shown) parts.push(plural(shown, noun));
          else if (noun) none.push(t.getAttribute("data-ams-none") || "Nothing here matches yet.");
        });
        if (state) {
          FL.from(state, {
            duration: 0.5, ease: "power2.inOut", absolute: true, prune: true,
            onComplete: function () { settle(items); },
            onEnter: function (els) { return G.fromTo(els, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out", onComplete: function () { settle(els); } }); },
            onLeave: function (els) { return G.to(els, { opacity: 0, y: 12, duration: 0.22, ease: "power1.in", onComplete: function () { settle(els); } }); }
          });
        }
        if (status) {
          var label = btn.getAttribute("data-ams-label") || btn.textContent.trim();
          var text = parts.length ? "Showing " + parts.join(" and ") + (key === "all" ? "" : " for " + label) + "." : "";
          if (none.length) text = (text ? text + " " : "") + none.join(" ");
          typeLine(status, text);
        }
        if (guide) {
          var poses = $all(guide, "[data-pose]");
          var want = btn.getAttribute("data-ams-pose") || "all";
          var found = false;
          each(poses, function (p) { if (p.getAttribute("data-pose") === want) found = true; });
          each(poses, function (p) {
            var on = found ? p.getAttribute("data-pose") === want : p.getAttribute("data-pose") === "all";
            p.setAttribute("aria-hidden", on ? "false" : "true");
          });
        }
      }
    });
  }

  /* typed status for sighted users, final text announced once through a live region */
  function typeLine(el, text) {
    var vis = el.querySelector("[data-ams-type]");
    var live = el.querySelector("[data-ams-live]");
    if (live) live.textContent = text;
    if (!vis) return;
    if (!hasGsap || !motion) { vis.textContent = text; return; }
    if (vis._amsTween) vis._amsTween.kill();
    var o = { n: 0 };
    vis._amsTween = G.to(o, {
      n: text.length, duration: Math.min(1.1, 0.018 * text.length), ease: "none",
      onUpdate: function () { vis.textContent = text.slice(0, Math.round(o.n)); }
    });
  }

  /* scroll stage: a sticky panel inside a tall section; the step under the viewport centre is active */
  function initStage(root) {
    each($all(root, "[data-ams-stage]"), function (stage) {
      var steps = $all(stage, ".ams-stage__step");
      var copies = $all(stage, ".ams-stage__copy[data-step]");
      var rail = $all(stage, ".ams-stage__rail button");
      function set(i) {
        if (stage.getAttribute("data-active") === String(i)) return;
        stage.setAttribute("data-active", String(i));
        each(copies, function (c) { c.classList.toggle("is-active", c.getAttribute("data-step") === String(i)); });
        each(rail, function (b, k) {
          b.classList.toggle("is-active", k === i);
          b.classList.toggle("is-done", k < i);
          if (k === i) b.setAttribute("aria-current", "step"); else b.removeAttribute("aria-current");
        });
      }
      if ("IntersectionObserver" in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { if (e.isIntersecting) set(Number(e.target.getAttribute("data-step"))); });
        }, { rootMargin: "-50% 0px -50% 0px", threshold: 0 });
        each(steps, function (s) { io.observe(s); });
      } else {
        set(0);
      }
      each(rail, function (b, k) {
        b.addEventListener("click", function () {
          var s = steps[k];
          if (!s) return;
          var r = s.getBoundingClientRect();
          var y = window.pageYOffset + r.top + r.height / 2 - window.innerHeight / 2 + 2;
          window.scrollTo({ top: y, behavior: motion ? "smooth" : "auto" });
        });
      });
    });
  }

  /* hero: one orchestrated entrance plus a gentle scrub as it scrolls away; logos stay still */
  function initHero(root) {
    if (!hasGsap || !motion) return;
    each($all(root, "[data-ams-hero]"), function (hero) {
      var art = hero.querySelector("[data-ams-hero-art]");
      var bits = $all(hero, "[data-ams-hero-in]");
      var tl = G.timeline({ defaults: { ease: "power3.out" } });
      if (art) tl.fromTo(art, { scale: 1.06 }, { scale: 1, duration: 1.4, ease: "power2.out" }, 0);
      if (bits.length) tl.fromTo(bits, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.7, stagger: 0.1 }, 0.15);
      if (art && art.hasAttribute("data-ams-scrub")) {
        G.to(art, { yPercent: 8, ease: "none", scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: true } });
      }
    });
  }

  /* compare tray (build desk): checkbox state is the truth, the tray only reflects it */
  function initCompare(root) {
    each($all(root, "[data-ams-compare-scope]"), function (scope) {
      var max = Number(scope.getAttribute("data-ams-compare-max") || 3);
      var boxes = $all(scope, "input[data-ams-compare]");
      var tray = root.querySelector(scope.getAttribute("data-ams-tray"));
      var panel = root.querySelector(scope.getAttribute("data-ams-panel"));
      if (!tray) return;
      var list = tray.querySelector("[data-ams-tray-list]");
      var count = tray.querySelector("[data-ams-tray-count]");
      var go = tray.querySelector("[data-ams-tray-go]");
      var clear = tray.querySelector("[data-ams-tray-clear]");
      function chosen() { return Array.prototype.filter.call(boxes, function (b) { return b.checked; }); }
      function sync() {
        var c = chosen();
        each(boxes, function (b) {
          b.disabled = !b.checked && c.length >= max;
          var row = b.closest("[data-ams-row]");
          if (row) row.classList.toggle("is-selected", b.checked);
        });
        tray.classList.toggle("is-open", c.length > 0);
        tray.setAttribute("aria-hidden", c.length > 0 ? "false" : "true");
        if (count) count.textContent = c.length + " of " + max + " chosen";
        if (go) go.disabled = c.length < 2;
        if (list) {
          while (list.firstChild) list.removeChild(list.firstChild);
          c.forEach(function (b) {
            var li = document.createElement("li");
            li.textContent = b.getAttribute("data-ams-name") || "";
            list.appendChild(li);
          });
        }
      }
      each(boxes, function (b) { b.addEventListener("change", sync); });
      if (clear) clear.addEventListener("click", function () {
        each(boxes, function (b) { b.checked = false; });
        sync();
        if (panel) panel.hidden = true;
      });
      if (go && panel) go.addEventListener("click", function () {
        var c = chosen();
        var cols = panel.querySelectorAll("[data-ams-col]");
        each(cols, function (col) {
          var on = c.some(function (b) { return b.value === col.getAttribute("data-ams-col"); });
          col.hidden = !on;
        });
        panel.hidden = false;
        if (hasGsap && motion) G.fromTo(panel, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.45, ease: "power2.out", onComplete: function () { settle([panel]); } });
        panel.scrollIntoView({ behavior: motion ? "smooth" : "auto", block: "start" });
      });
      sync();
    });
  }

  /* sort rows by a numeric data attribute; Flip animates the reorder */
  function initSort(root) {
    each($all(root, "[data-ams-sort]"), function (sel) {
      var list = root.querySelector(sel.getAttribute("data-ams-sort"));
      if (!list) return;
      sel.addEventListener("change", function () {
        var rows = Array.prototype.slice.call(list.querySelectorAll("[data-ams-row]"));
        var key = sel.value;
        var state = hasGsap && motion ? FL.getState(rows) : null;
        rows.sort(function (a, b) {
          var av = Number(a.getAttribute("data-" + key.split(":")[0]) || 0);
          var bv = Number(b.getAttribute("data-" + key.split(":")[0]) || 0);
          return key.split(":")[1] === "desc" ? bv - av : av - bv;
        });
        rows.forEach(function (r) { list.appendChild(r); });
        if (state) FL.from(state, { duration: 0.5, ease: "power2.inOut", onComplete: function () { settle(rows); } });
      });
    });
  }

  /* in-page links inside the mockup: handle them here so the theme's own anchor scroller does not */
  function initAnchors(root) {
    root.addEventListener("click", function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#ams-"]');
      if (!a || !root.contains(a)) return;
      var t = document.getElementById(a.getAttribute("href").slice(1));
      if (!t) return;
      e.preventDefault();
      e.stopPropagation();
      t.scrollIntoView({ behavior: motion ? "smooth" : "auto", block: "start" });
      if (!t.hasAttribute("tabindex")) t.setAttribute("tabindex", "-1");
      setTimeout(function () { t.focus({ preventScroll: true }); }, motion ? 600 : 0);
    }, true);
  }
})();
